/**
 * generateCtaCardImage
 * 
 * Generates a professional CTA card as a JPEG Blob using HTML Canvas.
 * This card is published as an additional Story slide on Instagram/Facebook.
 * 
 * Instagram Stories API does NOT support link stickers (confirmed by Meta docs).
 * This is the professional workaround — a visually rich CTA slide.
 */

export async function generateCtaCardImage(
  ctaButtonText: string,
  ctaUrl: string
): Promise<Blob> {
  const width = 1080;
  const height = 1920;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  // === Background gradient ===
  const bgGrad = ctx.createLinearGradient(0, 0, width, height);
  bgGrad.addColorStop(0, '#0f0c29');
  bgGrad.addColorStop(0.5, '#302b63');
  bgGrad.addColorStop(1, '#24243e');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, width, height);

  // === Decorative circles ===
  ctx.globalAlpha = 0.05;
  ctx.fillStyle = '#667eea';
  ctx.beginPath();
  ctx.arc(150, 300, 200, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#764ba2';
  ctx.beginPath();
  ctx.arc(930, 1600, 250, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 0.03;
  ctx.fillStyle = '#667eea';
  ctx.beginPath();
  ctx.arc(540, 960, 400, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  // === Arrow up icon ===
  ctx.save();
  ctx.translate(540, 680);
  ctx.globalAlpha = 0.3;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(0, 0, 60, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = 0.8;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(0, -30);
  ctx.lineTo(18, 5);
  ctx.lineTo(6, 5);
  ctx.lineTo(6, 25);
  ctx.lineTo(-6, 25);
  ctx.lineTo(-6, 5);
  ctx.lineTo(-18, 5);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  // === Link emoji text ===
  ctx.globalAlpha = 0.9;
  ctx.font = '64px Arial, Helvetica, sans-serif';
  ctx.fillStyle = '#a5b4fc';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('🔗', 540, 820);

  // === CTA Button with gradient ===
  const btnX = 120;
  const btnY = 910;
  const btnW = 840;
  const btnH = 110;
  const btnR = 55;

  // Button shadow
  ctx.shadowColor = 'rgba(102, 126, 234, 0.5)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 8;

  // Button gradient
  const btnGrad = ctx.createLinearGradient(btnX, btnY, btnX + btnW, btnY);
  btnGrad.addColorStop(0, '#667eea');
  btnGrad.addColorStop(1, '#764ba2');

  // Rounded rect
  ctx.beginPath();
  ctx.moveTo(btnX + btnR, btnY);
  ctx.lineTo(btnX + btnW - btnR, btnY);
  ctx.arcTo(btnX + btnW, btnY, btnX + btnW, btnY + btnR, btnR);
  ctx.lineTo(btnX + btnW, btnY + btnH - btnR);
  ctx.arcTo(btnX + btnW, btnY + btnH, btnX + btnW - btnR, btnY + btnH, btnR);
  ctx.lineTo(btnX + btnR, btnY + btnH);
  ctx.arcTo(btnX, btnY + btnH, btnX, btnY + btnH - btnR, btnR);
  ctx.lineTo(btnX, btnY + btnR);
  ctx.arcTo(btnX, btnY, btnX + btnR, btnY, btnR);
  ctx.closePath();
  ctx.fillStyle = btnGrad;
  ctx.fill();

  // Button border
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.globalAlpha = 0.3;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Button text
  ctx.font = 'bold 42px Arial, Helvetica, sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 2;
  const displayButtonText = ctaButtonText || 'Sprawdź teraz →';
  // Truncate if too long
  const maxBtnTextLen = 28;
  const truncatedBtnText = displayButtonText.length > maxBtnTextLen
    ? displayButtonText.substring(0, maxBtnTextLen - 1) + '…'
    : displayButtonText;
  ctx.fillText(truncatedBtnText, 540, btnY + btnH / 2);

  // Reset shadow
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // === URL display ===
  const displayUrl = ctaUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
  ctx.font = '30px Arial, Helvetica, sans-serif';
  ctx.fillStyle = '#a5b4fc';
  ctx.globalAlpha = 0.85;
  ctx.textAlign = 'center';
  ctx.fillText(displayUrl, 540, 1090);

  // === Divider line ===
  ctx.globalAlpha = 0.15;
  ctx.strokeStyle = '#a5b4fc';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(340, 1140);
  ctx.lineTo(740, 1140);
  ctx.stroke();

  // === Hint text ===
  ctx.globalAlpha = 0.4;
  ctx.font = '26px Arial, Helvetica, sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.fillText('Kliknij link w bio lub skopiuj adres ↗', 540, 1190);

  // === Dots decoration ===
  ctx.globalAlpha = 0.3;
  ctx.fillStyle = '#ffffff';
  [490, 520, 550, 580, 610].forEach((x, i) => {
    ctx.globalAlpha = i === 2 ? 0.6 : 0.25;
    ctx.beginPath();
    ctx.arc(x, 1260, 4, 0, Math.PI * 2);
    ctx.fill();
  });

  ctx.globalAlpha = 1;

  // === Convert to JPEG Blob ===
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to generate CTA card image'));
      },
      'image/jpeg',
      0.92
    );
  });
}
